/**
 * THE APPOINTMENT RECEIPT, AS RENDERABLE FACTS — AND NOTHING ELSE.
 *
 * Product decisions 1A/2A (Lura/docs/2026-09-12-receipt-decision-packet.md)
 * and Backend's `booking-v1@1.7.0` (pinned in scripts/lib/booking-contract.js):
 *
 *   - Location and confirmation code are REQUIRED on both the immediate outcome
 *     and the durable `/a/<token>` view. They are appointment-scoped facts
 *     committed at creation.
 *   - Null — or blank, or an EMPTY `AppointmentAccessView.businessName` — means
 *     the fact is unavailable. The row still renders, with the approved
 *     "Unavailable" value and a visible notice. A row never disappears.
 *   - Nothing is inferred. This module's inputs are the appointment payload
 *     only; a professional profile cannot be passed in, so a profile fallback is
 *     not something a caller can reach by accident. The confirmation code is
 *     shown verbatim and never generated, reformatted or reconstructed.
 *   - An unrecognised `locationKind` is unavailable, not a guess.
 */

/** A fact the server supplied, or the explicit absence of one. */
export type ReceiptFact = { available: true; value: string } | { available: false };

export interface Receipt {
  /** The full client-visible address for an AT_BUSINESS appointment. */
  location: ReceiptFact;
  confirmationCode: ReceiptFact;
  businessName: ReceiptFact;
  /** True when any required receipt fact is unavailable: the notice must show. */
  incomplete: boolean;
}

/** The appointment fields a receipt is read from. No profile field exists here. */
export interface ReceiptSource {
  confirmationCode: string | null | undefined;
  businessName: string | null | undefined;
  locationKind: string | null | undefined;
  addressDisplay: string | null | undefined;
}

const UNAVAILABLE: ReceiptFact = Object.freeze({ available: false });

/** Present and non-blank, verbatim (never trimmed or reformatted), or unavailable. */
function fact(value: string | null | undefined): ReceiptFact {
  return typeof value === "string" && value.trim().length > 0 ? { available: true, value } : UNAVAILABLE;
}

/**
 * The location a client can act on. Today the only kind is AT_BUSINESS, whose
 * location is the committed address. Any other or missing kind — including a
 * future virtual or mobile kind whose instructions 1.7.0 does not carry —
 * is unavailable rather than inferred from the address field.
 */
function location(source: ReceiptSource): ReceiptFact {
  if (source.locationKind !== "AT_BUSINESS") return UNAVAILABLE;
  return fact(source.addressDisplay);
}

export function receiptFrom(source: ReceiptSource): Receipt {
  const receipt = {
    location: location(source),
    confirmationCode: fact(source.confirmationCode),
    businessName: fact(source.businessName),
  };
  return {
    ...receipt,
    incomplete: !receipt.location.available || !receipt.confirmationCode.available || !receipt.businessName.available,
  };
}
